import { forwardRef, useState } from 'react';
import { Box } from '../../Layout/Box';
import { getRatio } from './getRatio';
import type { ImageProps } from './types';

/** Default placeholder: 1x1 transparent PNG (no dependency on app assets). */
const DEFAULT_PLACEHOLDER_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

/**
 * Lazy-loaded image with optional aspect-ratio container and configurable placeholder.
 * Accessibility: pass alt (meaningful text) for content images, or decorative for presentational images.
 *
 * @example
 * <Image src="/photo.jpg" ratio="16/9" alt="Photo of tree canopy" />
 * <Image src="/decoration.svg" decorative />
 * <Image src="/photo.jpg" placeholderSrc="/placeholder.svg" effect="blur" alt="Detail" />
 */
export const Image = forwardRef<HTMLSpanElement, ImageProps>(
  (
    {
      ratio,
      disabledEffect = false,
      effect = 'blur',
      placeholderSrc,
      sx,
      decorative,
      alt,
      src,
      style,
      onLoad,
      loading = 'lazy',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      children: _children,
      ...other
    },
    ref
  ) => {
    const [loaded, setLoaded] = useState(false);

    const imgAlt = decorative ? '' : alt ?? '';
    const imgRole = decorative ? ('presentation' as const) : undefined;
    const showBlur = !disabledEffect && effect === 'blur';
    const resolvedPlaceholder = placeholderSrc ?? DEFAULT_PLACEHOLDER_SRC;

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true);
      onLoad?.(e);
    };

    const content = (
      <span
        className="wrapper"
        style={{ display: 'block', width: '100%', height: '100%', position: 'relative' }}
      >
        {showBlur && (
          <img
            aria-hidden
            src={resolvedPlaceholder}
            alt=""
            draggable={false}
            tabIndex={-1}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'blur(20px)',
              transform: 'scale(1.1)',
              opacity: loaded ? 0 : 1,
              transition: 'opacity 0.5s ease',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />
        )}
        <img
          {...other}
          src={src}
          alt={imgAlt}
          role={imgRole}
          loading={loading}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: showBlur ? (loaded ? 1 : 0) : 1,
            transition: showBlur ? 'opacity 0.5s ease' : undefined,
            ...style,
          }}
        />
      </span>
    );

    const boxRef = ref as React.Ref<HTMLDivElement>;

    if (ratio) {
      return (
        <Box
          ref={boxRef}
          component="span"
          sx={{
            width: 1,
            lineHeight: 1,
            display: 'block',
            overflow: 'hidden',
            position: 'relative',
            pt: getRatio(ratio),
            '& .wrapper': {
              top: 0,
              left: 0,
              width: 1,
              height: 1,
              position: 'absolute',
            },
            ...sx,
          }}
        >
          {content}
        </Box>
      );
    }

    return (
      <Box
        ref={boxRef}
        component="span"
        sx={{
          lineHeight: 1,
          display: 'block',
          overflow: 'hidden',
          position: 'relative',
          '& .wrapper': { width: 1, height: 1 },
          ...sx,
        }}
      >
        {content}
      </Box>
    );
  }
);

Image.displayName = 'Image';
